import axios from 'axios'
import { apiHeaders } from './apiConstants'
import { getUserFromLocalStorage, removeUserFromLocalStorage } from './utils'
import { localConstants } from './localizationConstants'
import { updateIsLoading, updateToastData } from './toastSlice'
import CryptoJS from 'crypto-js'
import { navigationService } from '../routes/NavigationProvider'

let loaderUpdated = 0

const customAxios = axios.create({
	baseURL: process.env.REACT_APP_API_BASE_URL,
	headers: {
		'Content-Type': 'application/json',
		// useCredentials: true,
		deviceType: 'Web',
	},
	timeout: 300000,
})

customAxios.interceptors.request.use(
	(config) => {
		const user = getUserFromLocalStorage()

		const access_token = user?.access_token
		if (user && access_token) {
			config.headers.authorization = `Bearer ${access_token}`
		}

		if (!config.headers[apiHeaders.preventDefaultLoader]) {
			const { store } = require('./store')
			store.dispatch(updateIsLoading(true))
			loaderUpdated += 1
		}

		// Remove the custom header before the request is sent
		delete config.headers[apiHeaders.preventDefaultLoader]

		if (process.env.REACT_APP_ENV === 'production') {
			// Check if data exists and encrypt it
			if (config.data) {
				// Encrypt the request body
				const encryptedRequestBody = encryptRequestBody(
					config.data, // Pass the plain data
					process.env.REACT_APP_REQUEST_CRYPTO_HEX_KEY, // Secret key
					process.env.REACT_APP_REQUEST_CRYPTO_IV_KEY // IV
				)

				// Replace the plain body with the encrypted data
				config.data = encryptedRequestBody
			}
		}

		return config
	},
	(error) => {
		return Promise.reject(error)
	}
)

customAxios.interceptors.response.use(
	(response) => {
		if (!response.config.headers[apiHeaders.preventDefaultToast]) {
			if (process.env.REACT_APP_ENV === 'production') {
				// If the response is successful, just return the response
				const decryptedResponse = decryptResponse(
					response.data,
					process.env.REACT_APP_RESPONSE_CRYPTO_HEX_KEY,
					process.env.REACT_APP_RESPONSE_CRYPTO_IV_KEY
				)
				response.data = decryptedResponse
			}
			if (response?.data?.message) {
				const { store } = require('./store')

				store.dispatch(
					updateToastData({
						showToast: true,
						title: '',
						message: response?.data?.message,
						status: response?.data?.status,
						direction: response?.response?.data?.fileContainsError ? 'left' : 'right',
					})
				)
			}
		}
		if (!response.config.headers[apiHeaders.preventDefaultLoader]) {
			loaderUpdated -= 1
			const { store } = require('./store')
			if (
				loaderUpdated < 1
				// && checkIfGetS3LinkApi(response.config.url)
			) {
				setTimeout(() => {
					store.dispatch(updateIsLoading(false))
				}, 1000)
			}
		}

		// Remove the custom header before the request is sent
		delete response?.config?.headers[apiHeaders.preventDefaultLoader]
		delete response?.config?.headers[apiHeaders.preventDefaultToast]

		return response
	},
	(error) => {
		let errorMessage =
			error?.code === 'ERR_NETWORK'
				? localConstants.contactAdminSomethingWrong
				: error?.response?.data?.error_message ??
				  error?.response?.data?.error ??
				  error?.response?.data?.errors ??
				  error?.message ??
				  localConstants.contactAdminSomethingWrong
		error.errorMessage = errorMessage

		// In your 401 error handling
		if (error?.response?.status === 401) {
			// Reset the loader counter
			loaderUpdated = 0

			removeUserFromLocalStorage()

			const { store } = require('./store')

			// Explicitly set loading to false
			store.dispatch(updateIsLoading(false))

			store.dispatch(
				updateToastData({
					showToast: true,
					title: 'Session Expired',
					message: 'This user is currently logged in on another device',
					status: 0,
					direction: 'right',
				})
			)

			// Navigate to home page after a short delay
			setTimeout(() => {
				navigationService.navigateToRoute('/')

				// Additional safeguard to ensure loader is stopped
				store.dispatch(updateIsLoading(false))
			}, 2000)

			// Return a resolved promise to prevent further error handling
			return Promise.resolve()
		}

		if (!error.config.headers[apiHeaders.preventDefaultToast]) {
			// Skip if the error was already handled (401)
			if (error.handled) {
				return Promise.reject(error)
			}
			if (errorMessage) {
				const errorKey = typeof errorMessage === 'string' ? [] : Object.keys(errorMessage[0]?.errors)[0]
				let subTitle
				if (error.response.data.fileContainsError) {
					subTitle = error.response.data.message
				} else {
					subTitle = typeof errorMessage === 'string' ? errorMessage : errorMessage[0]?.errors[errorKey]
				}
				const { store } = require('./store')
				store.dispatch(
					updateToastData({
						showToast: true,
						title: '',
						message: subTitle,
						status: error.response.data.status,
						multipleError: Array.isArray(errorMessage) ? errorMessage : [],
						direction: error?.response?.data?.fileContainsError ? 'left' : 'right',
					})
				)
			}
		}

		if (!error.config.headers[apiHeaders.preventDefaultLoader]) {
			const { store } = require('./store')
			loaderUpdated -= 1
			if (
				loaderUpdated < 1
				//  && checkIfGetS3LinkApi(error.config.url)
			) {
				setTimeout(() => {
					store.dispatch(updateIsLoading(false))
				}, 1000)
			}
		}

		if (error?.code === 'ERR_NETWORK') {
			console.log('network error')
		}

		if (error?.response?.status === 404) {
			console.log('not found')
		}
		if (error?.response?.status === 400) {
			console.log('bad request')
		}

		delete error?.config?.headers[apiHeaders.preventDefaultLoader]
		delete error?.config?.headers[apiHeaders.preventDefaultToast]

		return Promise.reject(error)
	}
)

export default customAxios

export const encryptRequestBody = (data, secretKey, iv) => {
	try {
		const stringifiedData = JSON.stringify(data) // Convert the data to a string
		const parsedIv = CryptoJS.enc.Utf8.parse(iv) // Parse the IV
		const encrypted = CryptoJS.AES.encrypt(stringifiedData, secretKey, { iv: parsedIv }) // Encrypt the data
		return encrypted.toString() // Convert encrypted data to string
	} catch (error) {
		console.error('Error encrypting request body:', error)
		throw error
	}
}

export const decryptResponse = (encryptedData, secretKey, iv) => {
	try {
		const parsedIv = CryptoJS.enc.Utf8.parse(iv)
		const decryptedBytes = CryptoJS.AES.decrypt(encryptedData, secretKey, { iv: parsedIv })
		const decryptedData = JSON.parse(decryptedBytes.toString(CryptoJS.enc.Utf8))
		return decryptedData
	} catch (e) {
		console.error('Error while trying to decrypt data', e)
		throw e
	}
}
